import { useEffect, useState, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  VStack,
  HStack,
  Text,
  Grid,
  Divider,
  Checkbox,
  Select,
  Tooltip,
} from '@chakra-ui/react'
import { 
  createBudgetSchema, updateBudgetSchema, type CreateBudgetFormData, type UpdateBudgetFormData,
  editIssuedBudgetSchema, editTruckTripExpenseSchema, editPartsExpenseSchema, editFundsForStackRunSchema, editDriverCashAdvanceSchema, editHelperCashAdvanceSchema,
  type EditIssuedBudgetFormData, type EditTruckTripExpenseFormData, type EditPartsExpenseFormData, type EditFundsForStackRunFormData, type EditDriverCashAdvanceFormData, type EditHelperCashAdvanceFormData
  } from '@/lib/validations/auth' // renamed type for clarity
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useFieldArray } from "react-hook-form"
import { useParams } from 'react-router-dom'

interface BudgetManagamentFormProps {
  budgetManagementId?: number
  budgetManagementExpenseType?: string
  onSuccess: () => void
  onCancel: () => void
}

export function BudgetManagementForm({ budgetManagementId, budgetManagementExpenseType, onSuccess, onCancel }: BudgetManagamentFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isEditMode = !!budgetManagementId
  const { showError, showSuccess } = useToastNotification()

  type dayNightShift = 'Day' | 'Night'
  
  const [dayNightShiftOptions, setdayNightShiftOptions] = useState<dayNightShift[]>(['Day', 'Night'])
  const [driverList, setDriverList] = useState<{ id: number; first_name: string; last_name: string }[]>([])
  const [helperList, setHelperList] = useState<{ id: number; first_name: string; last_name: string }[]>([])
  const [truckList, setTruckList] = useState<{ id: number; plate_number: string }[]>([])

      
      // Map display names to schema literal values
      const getExpenseTypeValue = (displayName?: string): string => {
        switch (displayName) {
          case "Issued Budget":
            return "issuedBudget"
          case "Truck Expense":
            return "truckTripExpense"
          case "Parts Expense":
            return "partsExpense"
          case "Other Expense":
            return "fundsForStackRun"
          case "Driver Cash Advance":
            return "driverCashAdvance"
          case "Helper Cash Advance":
            return "helperCashAdvance"
          default:
            return "issuedBudget"
        }
      }
    
      // Get the appropriate schema based on expense type (memoized to prevent re-renders)
      const schema = useMemo(() => {
        const selectedSchema = (() => {
          switch (budgetManagementExpenseType) {
            case "Issued Budget":
              return editIssuedBudgetSchema
            case "Truck Expense":
              return editTruckTripExpenseSchema
            case "Parts Expense":
              return editPartsExpenseSchema
            case "Other Expense":
              return editFundsForStackRunSchema
            case "Driver Cash Advance":
              return editDriverCashAdvanceSchema
            case "Helper Cash Advance":
              return editHelperCashAdvanceSchema
            default:
              return editIssuedBudgetSchema
          }
        })()
        console.log('[BudgetForm] Using schema for expense type:', budgetManagementExpenseType, selectedSchema)
        return selectedSchema
      }, [budgetManagementExpenseType])

  const {
    register,
    control,
    watch,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    setValue,
  } = useForm<CreateBudgetFormData | EditTruckTripExpenseFormData | EditPartsExpenseFormData | EditFundsForStackRunFormData | EditDriverCashAdvanceFormData | EditHelperCashAdvanceFormData>({
    resolver: zodResolver(isEditMode ? schema : createBudgetSchema),
    defaultValues: {
      expenseItems: [],
    },
  })
  
  const { fields, append, remove } = useFieldArray({
    control,
    name: "expenseItems",
    })
  const selectedType = watch("expenseType")

  useEffect(() => {
    if (!isEditMode) {
      reset({
        expenseType: selectedType as any,
        expenseItems: [],
      })
    }
  }, [selectedType, isEditMode, reset])

  useEffect(() => {
    const fetchData = async () => {
        try {
          const driverDataResponse = await apiClient.get(`drivers`);
          const driverData = driverDataResponse.data?.data || driverDataResponse.data || [];
          setDriverList(driverData);

          const helperDataResponse = await apiClient.get(`helpers`);
          const helperData = helperDataResponse.data?.data || helperDataResponse.data || [];
          setHelperList(helperData);

          const truckDataResponse = await apiClient.get(`trucks/truck-list`);
          const truckData = truckDataResponse.data?.data || truckDataResponse.data || [];
          setTruckList(truckData);

          if (isEditMode){
            // Grab existing data based on expense type and id
            if (budgetManagementExpenseType === "Issued Budget") {
              const response = await apiClient.get(`/budget/issued-budget/${budgetManagementId}`);
              const budgetManagement = response.data?.data || response.data || {};
              reset({
                  shift: budgetManagement.shift,
                  dateIssued: budgetManagement.transaction_date,
                  source: budgetManagement.source,
                  amount: budgetManagement.amount,
              });
            }
            else if (budgetManagementExpenseType === "Truck Expense") {
              const response = await apiClient.get(`/budget/truck-trip-expense/${budgetManagementId}`);
              const budgetManagement = response.data?.data || response.data || {};
              reset({
                  shift: budgetManagement.shift,
                  helperID: budgetManagement.helper_id,
                  cashOnHand: budgetManagement.cash_on_hand,
                  issuedCashAmount: budgetManagement.issued_cash_amount,
                  transactionDate: budgetManagement.transaction_date,
              });
            }
            else if (budgetManagementExpenseType === "Parts Expense") {
              const response = await apiClient.get(`/budget/parts-expense/${budgetManagementId}`);
              const budgetManagement = response.data?.data || response.data || {};
              reset({
                  shift: budgetManagement.shift,
                  plateNumber: budgetManagement.plate_number,
                  receiptNO: budgetManagement.receipt_no,
                  quantity: budgetManagement.quantity,
                  article: budgetManagement.article,
                  amountPerItem: budgetManagement.amount_per_item,
                  transactionDate: budgetManagement.transaction_date,
              });
            }
            else if (budgetManagementExpenseType === "Other Expense") {
              const response = await apiClient.get(`/budget/funds-for-stack-run/${budgetManagementId}`);
              const budgetManagement = response.data?.data || response.data || {};
              reset({
                  shift: budgetManagement.shift,
                  remarks: budgetManagement.remarks,
                  amount: budgetManagement.amount,
                  transactionDate: budgetManagement.transaction_date,
              });
            }
            else if (budgetManagementExpenseType === "Driver Cash Advance") {
              const response = await apiClient.get(`/cash-advances/${budgetManagementId}?type=1`);
              const budgetManagement = response.data?.data || response.data || {};
              reset({
                shift: budgetManagement.shift,
                driverID: budgetManagement.driver_id,
                amount: budgetManagement.amount,
                transactionDate: budgetManagement.transaction_date,
              });
            }
            else if (budgetManagementExpenseType === "Helper Cash Advance") {
              const response = await apiClient.get(`/cash-advances/${budgetManagementId}?type=2`);
              const budgetManagement = response.data?.data || response.data || {};
              reset({
                shift: budgetManagement.shift,
                helperID: budgetManagement.helper_id,
                amount: budgetManagement.amount,
                transactionDate: budgetManagement.transaction_date,
              });
            }
          }
        } catch (error: any) {
          showError(error);
        }
      };
      fetchData();
    }, [isEditMode, budgetManagementExpenseType, budgetManagementId, reset, showError]);

  const onSubmit = async (data: any) => {
    setIsSubmitting(true)

    const formattedData = {
        ...data,
        expenseType: getExpenseTypeValue(budgetManagementExpenseType)
      }
    try
    {
      if (isEditMode) {
        // expenseTypeKey is no longer needed but kept for reference if later required

        switch (budgetManagementExpenseType) {
        case "Issued Budget":
          const editIssuedBudgetData = formattedData as EditIssuedBudgetFormData
          await apiClient.patch(`/budget/issued-budget/${budgetManagementId}`, {
            shift: editIssuedBudgetData.shift,
            transaction_date: editIssuedBudgetData.dateIssued,
            source: editIssuedBudgetData.source,
            amount: editIssuedBudgetData.amount,
          })
          showSuccess('Success', 'Issued Budget successfully updated')
          break

        case "Truck Expense":
          const editTruckTripExpenseData = formattedData as EditTruckTripExpenseFormData
          await apiClient.patch(`/budget/truck-trip-expense/${budgetManagementId}`, {
            shift: editTruckTripExpenseData.shift,
            helper_id: editTruckTripExpenseData.helperID,
            cash_on_hand: editTruckTripExpenseData.cashOnHand,
            issued_cash_amount: editTruckTripExpenseData.issuedCashAmount,
            transaction_date: editTruckTripExpenseData.transactionDate,
          })
          showSuccess('Success', 'Truck Trip Expense successfully updated')
          break

        case "Parts Expense":
          const editPartsExpenseData = formattedData as EditPartsExpenseFormData
          await apiClient.patch(`/budget/parts-expense/${budgetManagementId}`, {
            shift: editPartsExpenseData.shift,
            plate_number: editPartsExpenseData.plateNumber,
            receipt_no: editPartsExpenseData.receiptNO,
            quantity: editPartsExpenseData.quantity,
            article: editPartsExpenseData.article,
            amount_per_item: editPartsExpenseData.amountPerItem,
            transaction_date: editPartsExpenseData.transactionDate,
          })
          showSuccess('Success', 'Parts Expense successfully updated')
          break

        case "Other Expense":
          const editFundsForStackRunData = formattedData as EditFundsForStackRunFormData
          await apiClient.patch(`/budget/funds-for-stack-run/${budgetManagementId}`, {
            shift: editFundsForStackRunData.shift,
            remarks: editFundsForStackRunData.remarks,
            amount: editFundsForStackRunData.amount,
            transaction_date: editFundsForStackRunData.transactionDate,
          })
          showSuccess('Success', 'Other Expense successfully updated')
          break

        case "Driver Cash Advance":
          const editDriverCashAdvanceData = formattedData as EditDriverCashAdvanceFormData
          await apiClient.patch(`/cash-advances/${budgetManagementId}`, {
            type: 1,
            requestor_id: editDriverCashAdvanceData.driverID,
            amount: editDriverCashAdvanceData.amount,
            transaction_date: editDriverCashAdvanceData.transactionDate,
            shift: editDriverCashAdvanceData.shift,
          })
          showSuccess('Success', 'Driver Cash Advance successfully updated')
          break

        case "Helper Cash Advance":
          const editHelperCashAdvanceData = formattedData as EditHelperCashAdvanceFormData
          await apiClient.patch(`/cash-advances/${budgetManagementId}`, {
            type: 2,
            requestor_id: editHelperCashAdvanceData.helperID,
            amount: editHelperCashAdvanceData.amount,
            transaction_date: editHelperCashAdvanceData.transactionDate,
            shift: editHelperCashAdvanceData.shift,
          })
          showSuccess('Success', 'Helper Cash Advance successfully updated')
          break

        default:
          throw new Error('Unknown expense type')
      }
      }
      else { // Not in edit mode - create new record
        switch (data.expenseType) {
        case "issuedBudget":
          const issuedBudgetData = data as Extract<CreateBudgetFormData, { expenseType: "issuedBudget" }>
          await apiClient.post("/budget/issued-budget", {
            shift: issuedBudgetData.shift,
            transaction_date: issuedBudgetData.dateIssued,
            source: issuedBudgetData.source,
            amount: issuedBudgetData.amount,
            })
          showSuccess('Success', 'Issued Budget successfully created')
          break

        case "truckTripExpense":
          const truckTripData = data as Extract<CreateBudgetFormData, { expenseType: "truckTripExpense" }>

          await Promise.all(
            truckTripData.expenseItems.map((item) => apiClient.post('/budget/truck-trip-expense', {
              shift: item.shift,
              helper_id: item.helperID,
              cash_on_hand: item.cashOnHand,
              issued_cash_amount: item.issuedCashAmount,
              transaction_date: item.transactionDate,
              })
            )
          );
          showSuccess('Success', 'Truck Trip Expense successfully created')
          break

        case "partsExpense":
          const partsExpenseData = data as Extract<CreateBudgetFormData, { expenseType: "partsExpense" }>
          await Promise.all(
            partsExpenseData.expenseItems.map((item) => apiClient.post('/budget/parts-expense', {
              shift: item.shift,
              plate_number: item.plateNumber,
              receipt_no: item.receiptNO,
              quantity: item.quantity,
              article: item.article,
              amount_per_item: item.amountPerItem,
              transaction_date: item.transactionDate,
              })
            )
          );
          showSuccess('Success', 'Parts Expense successfully created')
          break

        case "fundsForStackRun":
          const fundsForStackRunData = data as Extract<CreateBudgetFormData, { expenseType: "fundsForStackRun" }>
          await Promise.all(
            fundsForStackRunData.expenseItems.map((item) => apiClient.post('/budget/funds-for-stack-run', {
              shift: item.shift,
              remarks: item.remarks,
              amount: item.amount,
              transaction_date: item.transactionDate,
              })
            )
          );
          showSuccess('Success', 'Other Expense successfully created')
          break

        case "driverCashAdvance":
          const driverCashAdvanceData = data as Extract<CreateBudgetFormData, { expenseType: "driverCashAdvance" }>
          await Promise.all(
            driverCashAdvanceData.expenseItems.map((item) => apiClient.post('/cash-advances', {
              type: 1,
              requestor_id: item.driverID,
              amount: item.amount,
              transaction_date: item.transactionDate,
              shift: item.shift,
              })
            )
          );
          showSuccess('Success', 'Driver Cash Advance successfully created')
          break

        case "helperCashAdvance":
          const helperCashAdvanceData = data as Extract<CreateBudgetFormData, { expenseType: "helperCashAdvance" }>
          await Promise.all(
            helperCashAdvanceData.expenseItems.map((item) => apiClient.post('/cash-advances', {
              type: 2,
              requestor_id: item.helperID,
              amount: item.amount,
              transaction_date: item.transactionDate,
              shift: item.shift,
              })
            )
          );
          showSuccess('Success', 'Helper Cash Advance successfully created')
          break
      }
    }

      onSuccess?.()

    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      
      <VStack spacing={4} align="stretch">
        {isEditMode === false && (
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Expense Type <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
            <Select
              placeholder="Select expense type"
              {...register("expenseType")}
            >
              <option value="issuedBudget">Issued Budget</option>
              <option value="truckTripExpense">Truck Trip Expense</option>
              <option value="partsExpense">Parts Expense</option>
              <option value="fundsForStackRun">Funds for Stack Run</option>
              <option value="driverCashAdvance">Driver Cash Advance</option>
              <option value="helperCashAdvance">Helper Cash Advance</option>
            </Select>
        </Grid>
        )}

        
        {/* Add Income Section */}
        {isEditMode === false && selectedType === 'issuedBudget' && (
          <VStack align="stretch" spacing={3}>
            <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
              <FormLabel pt={2} fontWeight="medium">
                Time of Shift <Text as="span" color="red.500">*</Text>
              </FormLabel>
              <Select placeholder="Time of Shift"
                  {...register('shift')}
                  >
                  {dayNightShiftOptions.map((shift) => ( <option key={shift} value={shift}> {shift} </option> ))}
                </Select>
            </Grid>

            <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
              <FormLabel pt={2} fontWeight="medium">
                Date Issued <Text as="span" color="red.500">
                  *
                </Text>
              </FormLabel>
              <Input type="date" {...register('dateIssued')} placeholder="Input the Date Issued"/>
            </Grid>

            <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
              Source <Text as="span" color="red.500">
                *
              </Text>
            </FormLabel>
            <Input type="text" {...register('source')} placeholder="Input the Source"/>
          </Grid>

          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
              Amount <Text as="span" color="red.500">
                *
              </Text>
            </FormLabel>
            <Input type="number" {...register('amount', { valueAsNumber: true })} placeholder="Input the Amount"/>
          </Grid>
          
          </VStack>
        )}

        {/* Truck Trip Expense Section */}
        {isEditMode === false && selectedType === "truckTripExpense" && (
          <VStack align="stretch" spacing={3}>
            <Grid templateColumns="1fr 2fr 1fr 1fr 1fr 0.5fr auto" gap={3}>
              <Text fontWeight="bold">Shift</Text>
              <Text fontWeight="bold">Helper</Text>
              <Text fontWeight="bold">Cash on Hand</Text>
              <Text fontWeight="bold">Issued Cash</Text>
              <Text fontWeight="bold">Date</Text>
            </Grid>

            {fields.map((field, index) => (
              <Grid
                key={field.id}
                templateColumns="1fr 2fr 1fr 1fr 1fr 0.5fr auto"
                gap={3}
                alignItems="center"
                >
                <Select {...register(`expenseItems.${index}.shift`)}>
                  <option value="Day">Day</option>
                  <option value="Night">Night</option>
                </Select>

                <Select {...register(`expenseItems.${index}.helperID`, { valueAsNumber: true })}>
                  {helperList.map((helper) => (
                    <option key={helper.id} value={helper.id}>
                      {helper.first_name} {helper.last_name}
                    </option>
                  ))}
                </Select>

                <Input
                  type="number"
                  {...register(`expenseItems.${index}.cashOnHand`, { valueAsNumber: true })}
                  />

                <Input
                  type="number"
                  {...register(`expenseItems.${index}.issuedCashAmount`, { valueAsNumber: true })}
                  />

                <Input
                  type="date"
                  {...register(`expenseItems.${index}.transactionDate`)}
                  />

                <Button size="sm" onClick={() => remove(index)}>
                  Remove
                </Button>
              </Grid>
            ))}

            <Button
              size="sm"
              onClick={() =>
                append({
                  shift: "",
                  helperID: 0,
                  cashOnHand: 0,
                  issuedCashAmount: 0,
                  transactionDate: "",
                })
              }
            >
              + Add Row
            </Button>
          </VStack>
        )}


        {/* Parts Expense Section */}
        {isEditMode === false && selectedType === "partsExpense" && (
          <VStack align="stretch" spacing={3}>
            <Grid templateColumns="1.1fr 1.25fr 1fr 0.75fr 1.75fr 1.25fr 1.25fr 0.5fr auto" gap={3}>
              <Text fontWeight="bold">Time of Shift</Text>
              <Text fontWeight="bold">Plate Number</Text>
              <Text fontWeight="bold">Receipt NO</Text>
              <Text fontWeight="bold">Quantity</Text>
              <Text fontWeight="bold">Article</Text>
              <Text fontWeight="bold">Amount</Text>
              <Text fontWeight="bold">Transaction Date</Text>
            </Grid>

            {fields.map((field, index) => (
              <Grid
                key={field.id}
                templateColumns="1.1fr 1.25fr 1fr 0.75fr 1.75fr 1.25fr 1.25fr 0.5fr auto" 
                gap={3}
                alignItems="center"
                >
                  <Select {...register(`expenseItems.${index}.shift`)}>
                    <option value="Day">Day</option>
                    <option value="Night">Night</option>
                  </Select>

                  <Select {...register(`expenseItems.${index}.plateNumber`)}>
                    {truckList.map((truck) => (
                      <option key={truck.id} value={truck.plate_number}>
                        {truck.plate_number}
                      </option>
                    ))}
                  </Select>

                  <Input
                    type="text"
                    {...register(`expenseItems.${index}.receiptNO`)}
                    />

                  <Input
                    type="number"
                    {...register(`expenseItems.${index}.quantity`, { valueAsNumber: true })}
                    />

                  <Input
                    type="text"
                    {...register(`expenseItems.${index}.article`)}
                  />

                  <Input
                    type="number"
                    {...register(`expenseItems.${index}.amountPerItem`, { valueAsNumber: true })}
                    />

                  <Input
                    type="date"
                    {...register(`expenseItems.${index}.transactionDate`)}
                    />

                  <Button size="sm" onClick={() => remove(index)}>
                    Remove
                  </Button>
              </Grid>
            ))}

            <Button
              size="sm"
              onClick={() =>
                append({
                  shift: "",
                  plateNumber: "",
                  receiptNO: "",
                  quantity: 0,
                  article: "",
                  amountPerItem: 0,
                  transactionDate: "",
                })
              }
            >
              + Add Row
            </Button>
          </VStack>
        )}


        {/* Funds For Stack Run Section */}
        {isEditMode === false && selectedType === "fundsForStackRun" && (
          <VStack align="stretch" spacing={3}>
            <Grid templateColumns="1.1fr 3fr 1fr 1fr 0.5fr auto" gap={3}>
              <Text fontWeight="bold">Time of Shift</Text>
              <Text fontWeight="bold">Remark</Text>
              <Text fontWeight="bold">Amount</Text>
              <Text fontWeight="bold">Date Issued</Text>
            </Grid>

            {fields.map((field, index) => (
              <Grid
                key={field.id}
                templateColumns="1.1fr 3fr 1fr 1fr 0.5fr auto" gap={3}
                alignItems="center"
                >
                <Select {...register(`expenseItems.${index}.shift`)}>
                  <option value="Day">Day</option>
                  <option value="Night">Night</option>
                </Select>

                <Input
                  type="text"
                  {...register(`expenseItems.${index}.remarks`)}
                  />

                <Input
                  type="number"
                  {...register(`expenseItems.${index}.amount`, { valueAsNumber: true })}
                  />

                <Input
                  type="date"
                  {...register(`expenseItems.${index}.transactionDate`)}
                  />

                <Button size="sm" onClick={() => remove(index)}>
                  Remove
                </Button>
              </Grid>
            ))}

            <Button
              size="sm"
              onClick={() =>
                append({
                  shift: "",
                  remarks: "",
                  amount: 0,
                  transactionDate: "",
                })
              }
              >
              + Add Row
            </Button>
          </VStack>
        )}


        {/* Driver Cash Expense Section */}
        {isEditMode === false && selectedType === "driverCashAdvance" && (
        <VStack align="stretch" spacing={3}>
          <Grid templateColumns="0.75fr 1.1fr 1fr 2fr 0.5fr auto" gap={3}>
            <Text fontWeight="bold">Amount</Text>
            <Text fontWeight="bold">Transaction Date</Text>
            <Text fontWeight="bold">Time of Shift</Text>
            <Text fontWeight="bold">Driver Name</Text>
          </Grid>

          {fields.map((field, index) => (
            <Grid
              key={field.id}
              templateColumns="0.75fr 1.1fr 1fr 2fr 0.5fr auto" gap={3}
              alignItems="center"
              >
                <Input
                  type="number"
                  {...register(`expenseItems.${index}.amount`, { valueAsNumber: true })}
                  />

                <Input
                  type="date"
                  {...register(`expenseItems.${index}.transactionDate`)}
                  />

                <Select {...register(`expenseItems.${index}.shift`)}>
                  <option value="Day">Day</option>
                  <option value="Night">Night</option>
                </Select>

                <Select {...register(`expenseItems.${index}.driverID`, { valueAsNumber: true })}>
                  {driverList.map((driver) => (
                    <option key={driver.id} value={driver.id}>
                      {driver.first_name} {driver.last_name}
                    </option>
                  ))}
                </Select>

                <Button size="sm" onClick={() => remove(index)}>
                  Remove
                </Button>
              </Grid>
            ))}

            <Button
              size="sm"
              onClick={() =>
                append({
                  amount: 0,
                  transactionDate: "",
                  shift: "",
                  driverID: 0,
                  })
                }
              >
              + Add Row
            </Button>
          </VStack>
        )}


        {/* Helper Cash Expense Section */}
        {isEditMode === false && selectedType === "helperCashAdvance" && (
        <VStack align="stretch" spacing={3}>
          <Grid templateColumns="0.75fr 1.1fr 1fr 2fr 0.5fr auto" gap={3}>
            <Text fontWeight="bold">Amount</Text>
            <Text fontWeight="bold">Transaction Date</Text>
            <Text fontWeight="bold">Time of Shift</Text>
            <Text fontWeight="bold">Helper Name</Text>
          </Grid>

          {fields.map((field, index) => (
            <Grid
              key={field.id}
              templateColumns="0.75fr 1.1fr 1fr 2fr 0.5fr auto" gap={3}
              alignItems="center"
              >
                <Input
                  type="number"
                  {...register(`expenseItems.${index}.amount`, { valueAsNumber: true })}
                  />

                <Input
                  type="date"
                  {...register(`expenseItems.${index}.transactionDate`)}
                  />

                <Select {...register(`expenseItems.${index}.shift`)}>
                  <option value="Day">Day</option>
                  <option value="Night">Night</option>
                </Select>

                <Select {...register(`expenseItems.${index}.helperID`, { valueAsNumber: true })}>
                  {helperList.map((helper) => (
                    <option key={helper.id} value={helper.id}>
                      {helper.first_name} {helper.last_name}
                    </option>
                  ))}
                </Select>

                <Button size="sm" onClick={() => remove(index)}>
                  Remove
                </Button>
              </Grid>
            ))}

            <Button
              size="sm"
              onClick={() =>
                append({
                  amount: 0,
                  transactionDate: "",
                  shift: "",
                  helperID: 0,
                  })
                }
              >
              + Add Row
            </Button>
          </VStack>
        )}

        
        {/* Show Validation Errors */}
                {Object.keys(errors).length > 0 && (
                  <Box bg="red.50" p={3} borderRadius="md" borderLeft="4px" borderColor="red.500">
                    <Text color="red.700" fontSize="sm" fontWeight="bold">Form Validation Errors:</Text>
                    {Object.entries(errors).map(([field, error]: [string, any]) => (
                      <Text key={field} color="red.600" fontSize="sm">
                        {field}: {error?.message || 'Invalid'}
                      </Text>
                    ))}
                  </Box>
                )}
                
                {/* Add Income Section */}
                {isEditMode === true && budgetManagementExpenseType === 'Issued Budget' && (
                  <VStack align="stretch" spacing={3}>
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
                      <FormLabel pt={2} fontWeight="medium">
                        Time of Shift <Text as="span" color="red.500">*</Text>
                      </FormLabel>
                      <Select placeholder="Time of Shift"
                        {...register('shift')}
                        >
                          {dayNightShiftOptions.map((shift) => ( <option key={shift} value={shift}> {shift} </option> ))}
                      </Select>
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
                      <FormLabel pt={2} fontWeight="medium">
                        Date Issued <Text as="span" color="red.500">
                          *
                        </Text>
                      </FormLabel>
                      <Input type="date" {...register('dateIssued')} placeholder="Input the Date Issued"/>
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
                    <FormLabel pt={2} fontWeight="medium">
                      Source <Text as="span" color="red.500">
                        *
                      </Text>
                    </FormLabel>
                    <Input type="text" {...register('source')} placeholder="Input the Source"/>
                  </Grid>
        
                  <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
                    <FormLabel pt={2} fontWeight="medium">
                      Amount <Text as="span" color="red.500">
                        *
                      </Text>
                    </FormLabel>
                    <Input type="number" {...register('amount', { valueAsNumber: true })} placeholder="Input the Amount"/>
                  </Grid>
                  
                  </VStack>
                )}
        
                {isEditMode === true && budgetManagementExpenseType === "Truck Expense" && (
                  <VStack align="stretch" spacing={3}>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Shift</FormLabel>
                      <Select {...register('shift')}>
                        <option value="Day">Day</option>
                        <option value="Night">Night</option>
                      </Select>
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Helper</FormLabel>
                      <Select {...register('helperID', { valueAsNumber: true })}>
                        {helperList.map((helper) => (
                          <option key={helper.id} value={helper.id}>
                            {helper.first_name} {helper.last_name}
                          </option>
                        ))}
                      </Select>
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Cash On Hand</FormLabel>
                        <Input
                          type="number"
                          {...register('cashOnHand', { valueAsNumber: true })}
                        />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Issued Cash Amount</FormLabel>
                      <Input
                        type="number"
                        {...register('issuedCashAmount', { valueAsNumber: true })}
                      />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Transaction Date</FormLabel>
                      <Input
                        type="date"
                        {...register('transactionDate')}
                      />
                    </Grid>
        
                  </VStack>
                )}
        
        
                {isEditMode === true && budgetManagementExpenseType === "Parts Expense" && (
                  <VStack align="stretch" spacing={3}>
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Shift</FormLabel>
                      <Select {...register('shift')}>
                        <option value="Day">Day</option>
                        <option value="Night">Night</option>
                      </Select>
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Plate Number</FormLabel>
                  
                      <Select {...register('plateNumber')}>
                        {truckList.map((truck) => (
                          <option key={truck.id} value={truck.plate_number}>
                            {truck.plate_number}
                          </option>
                        ))}
                      </Select>
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Receipt Number</FormLabel>
                        <Input
                          type="text"
                          {...register('receiptNO')}
                        />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Quantity</FormLabel>
                      <Input
                        type="number"
                        {...register('quantity', { valueAsNumber: true })}
                      />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Article</FormLabel>
                      <Input
                        type="text"
                        {...register('article')}
                      />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Amount Per Item</FormLabel>
                      <Input
                        type="number"
                        {...register('amountPerItem', { valueAsNumber: true })}
                      />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Transaction Date</FormLabel>
                      <Input
                        type="date"
                        {...register('transactionDate')}
                      />
                    </Grid>
        
                  </VStack>
                )}
        
        
                {isEditMode === true && budgetManagementExpenseType === "Other Expense" && (
                  <VStack align="stretch" spacing={3}>
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Shift</FormLabel>
                      <Select {...register('shift')}>
                        <option value="Day">Day</option>
                        <option value="Night">Night</option>
                      </Select>
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Remarks</FormLabel>
                      <Input
                        type="text"
                        {...register('remarks')}
                      />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Amount</FormLabel>
                      <Input
                        type="number"
                        {...register('amount', { valueAsNumber: true })}
                      />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Transaction Date</FormLabel>
                      <Input
                        type="date"
                        {...register('transactionDate')}
                      />
                    </Grid>
        
                  </VStack>
                )}
        
        
                {isEditMode === true && budgetManagementExpenseType === "Driver Cash Advance" && (
                  <VStack align="stretch" spacing={3}>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Amount</FormLabel>
                      <Input
                        type="number"
                        {...register('amount', { valueAsNumber: true })}
                      />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Transaction Date</FormLabel>
                      <Input
                        type="date"
                        {...register('transactionDate')}
                      />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Shift</FormLabel>
                      <Select {...register('shift')}>
                        <option value="Day">Day</option>
                        <option value="Night">Night</option>
                      </Select>
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Driver</FormLabel>
                      <Select {...register('driverID', { valueAsNumber: true })}>
                        {driverList.map((driver) => (
                          <option key={driver.id} value={driver.id}>
                            {driver.first_name} {driver.last_name}
                          </option>
                        ))}
                      </Select>
                    </Grid>
                  </VStack>
                )}
        
        
                {isEditMode === true && budgetManagementExpenseType === "Helper Cash Advance" && (
                  <VStack align="stretch" spacing={3}>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Amount</FormLabel>
                      <Input
                        type="number"
                        {...register('amount', { valueAsNumber: true })}
                      />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Transaction Date</FormLabel>
                      <Input
                        type="date"
                        {...register('transactionDate')}
                      />
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Shift</FormLabel>
                      <Select {...register('shift')}>
                        <option value="Day">Day</option>
                        <option value="Night">Night</option>
                      </Select>
                    </Grid>
        
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
                      <FormLabel>Helper</FormLabel>
                        <Select {...register('helperID', { valueAsNumber: true })}>
                          {helperList.map((helper) => (
                            <option key={helper.id} value={helper.id}>
                              {helper.first_name} {helper.last_name}
                            </option>
                          ))}
                        </Select>
                    </Grid>
                  </VStack>
                )}


        {/* Action Buttons */}
        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button
              variant="outline"
              onClick={onCancel}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText={isEditMode ? 'Saving...' : 'Creating...'}
          >
            {isEditMode ? 'Save' : 'Create New Budget Entry'}
          </Button>
        </HStack>
      </VStack>
    </Box>



  )
}